/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { InfoPopover } from "components/InfoPopover"
import InputError from "components/InputError"
import { UserParameters } from "features/userParameters/UserParametersSchema"
import { useEffect } from "react"
import DatePicker from "react-datepicker"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import "react-datepicker/dist/react-datepicker.css"
import { toDisplayDate } from "utils/dateUtils"

const GeneralParameters = () => {
  const {
    control,
    register,
    trigger,
    formState: { errors },
  } = useFormContext<UserParameters>()

  const allocationMode = useWatch({
    control,
    name: "allocationMode",
  })

  const retirementHorizonMonths = useWatch({
    control,
    name: "retirementHorizonMonths",
  })

  useEffect(() => {
    void trigger("glidePathLengthMonths")
  }, [retirementHorizonMonths, trigger])

  return (
    <fieldset className="form-group mb-3 p-3 border rounded" id="general-parameters-fieldset">
      <legend>General Parameters</legend>
      <div className="row py-1">
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor="initial-portfolio-dollars" className="col-form-label">
            Initial Portfolio Total
            <InfoPopover
              htmlContent={true}
              content="Include all financial assets, i.e., brokerage accounts, retirement accounts, CDs, money market accounts, etc., but exclude real estate, especially your primary residence!"
            />
          </label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              inputMode="numeric"
              className={`form-control ${errors.initialPortfolioDollars ? "is-invalid" : ""}`}
              id="initial-portfolio-dollars"
              aria-invalid={!!errors.initialPortfolioDollars}
              {...register("initialPortfolioDollars")}
            />
            <span className="input-group-text">.00</span>
            <InputError error={errors.initialPortfolioDollars} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor="retirement-start" className="col-form-label">
            Retirement Start
          </label>
          <Controller
            name="retirementStart"
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <>
                  <div className={`input-group ${error ? "is-invalid" : ""}`}>
                    <DatePicker
                      id="retirement-start"
                      ref={elem => {
                        if (elem) {
                          field.ref(elem.input)
                        }
                      }}
                      className={`form-control ${error ? "is-invalid" : ""}`}
                      dateFormat="MM/yyyy"
                      showMonthYearPicker
                      autoComplete="off"
                      placeholderText="mm/yyyy"
                      selected={toDisplayDate(field.value)}
                      onChange={(date: Date | null) => {
                        if (date) {
                          field.onChange({
                            month: date.getMonth() + 1,
                            year: date.getFullYear(),
                          })
                        } else {
                          field.onChange(null)
                        }
                      }}
                      onBlur={field.onBlur}
                    />
                  </div>
                  <InputError error={error} />
                </>
              )
            }}
          />
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor="retirement-horizon-months" className="col-form-label">
            Retirement Horizon
            <InfoPopover content="The anticipated length of the retirement, in months." />
          </label>
          <div className="input-group">
            <input
              inputMode="numeric"
              className={`form-control ${errors.retirementHorizonMonths ? "is-invalid" : ""}`}
              id="retirement-horizon-months"
              aria-invalid={!!errors.retirementHorizonMonths}
              {...register("retirementHorizonMonths")}
            />
            <span className="input-group-text">months</span>
            <InputError error={errors.retirementHorizonMonths} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor="final-value-pct-of-initial" className="col-form-label">
            Final Value Target
          </label>
          <div className="input-group">
            <input
              inputMode="decimal"
              className={`form-control ${errors.finalValuePctOfInitial ? "is-invalid" : ""}`}
              id="final-value-pct-of-initial"
              aria-invalid={!!errors.finalValuePctOfInitial}
              step="1"
              {...register("finalValuePctOfInitial")}
            />
            <span className="input-group-text">% of initial</span>
            <InputError error={errors.finalValuePctOfInitial} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor="initial-consumption-per-annum-dollars" className="col-form-label">
            Initial Consumption
            <InfoPopover content="You can optionally provide your initial per-annum spending. This amount shouldn't include any cash flows specified below." />
          </label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              inputMode="decimal"
              className={`form-control ${errors.initialConsumptionPerAnnumDollars ? "is-invalid" : ""}`}
              id="initial-consumption-per-annum-dollars"
              aria-invalid={!!errors.initialConsumptionPerAnnumDollars}
              {...register("initialConsumptionPerAnnumDollars")}
            />
            <span className="input-group-text">.00</span>
            <InputError error={errors.initialConsumptionPerAnnumDollars} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor="inflation-per-annum-pct" className="col-form-label">
            Inflation Rate
            <InfoPopover content="This per annum inflation projection is only used to translate future nominal cash flows to real dollar estimates. Since all simulations are in real, CPI-adjusted dollars, your inflation estimate has no other impact on the simulations." />
          </label>
          <div className="input-group">
            <input
              inputMode="decimal"
              className={`form-control ${errors.inflationPerAnnumPct ? "is-invalid" : ""}`}
              id="inflation-per-annum-pct"
              aria-invalid={!!errors.inflationPerAnnumPct}
              {...register("inflationPerAnnumPct")}
            />
            <span className="input-group-text">%</span>
            <InputError error={errors.inflationPerAnnumPct} />
          </div>
        </div>
        <fieldset className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <legend className="col-form-label">
            CAPE Model
            <InfoPopover content="The model to use when filtering historical cohorts by their initial CAPE ratios. <a href='https://earlyretirementnow.com/2022/10/05/building-a-better-cape-ratio/' target='_blank'>Learn more</a>." />
          </legend>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              id="ern-cape"
              value="ern"
              {...register("cape")}
            />
            <label className="form-check-label" htmlFor="ern-cape">
              ERN
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              id="ern-shiller"
              value="shiller"
              {...register("cape")}
            />
            <label className="form-check-label" htmlFor="ern-shiller">
              Shiller
            </label>
          </div>
        </fieldset>
        <fieldset className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <legend className="col-form-label">
            Asset Allocation
            <InfoPopover
              htmlContent={true}
              content={`
              <p><em>Static</em> to maintain a fixed allocation (rebalanced monthly).</p>
              <p><em>Glidepath</em> to transition the allocation from initial to final values, at a constant rate.</p>
              <p>See <a href='https://earlyretirementnow.com/2017/09/13/the-ultimate-guide-to-safe-withdrawal-rates-part-19-equity-glidepaths/' target='_blank'>this article</a> for guidance.</p>
            `}
            />
          </legend>
          <div className="form-check form-check-inline">
            <input
              className={`form-check-input ${errors.allocationMode ? "is-invalid" : ""}`}
              type="radio"
              id="static-allocation"
              value="static"
              {...register("allocationMode")}
            />
            <label className="form-check-label" htmlFor="static-allocation">
              Static
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className={`form-check-input${errors.allocationMode ? " is-invalid" : ""}`}
              type="radio"
              id="glide-path-allocation"
              value="glide path"
              {...register("allocationMode")}
            />
            <label className="form-check-label" htmlFor="glide-path-allocation">
              Glide Path
            </label>
          </div>
        </fieldset>

        {allocationMode === "glide path" && (
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2" id="glide-path-length-container">
            <label htmlFor="glide-path-length-months" className="col-form-label">
              Glide Path Length
            </label>
            <div className="input-group">
              <input
                inputMode="numeric"
                className={`form-control${errors.glidePathLengthMonths ? " is-invalid" : ""}`}
                id="glide-path-length-months"
                aria-invalid={!!errors.glidePathLengthMonths}
                {...register("glidePathLengthMonths")}
              />
              <span className="input-group-text">months</span>
              <InputError error={errors.glidePathLengthMonths} />
            </div>
          </div>
        )}
      </div>
    </fieldset>
  )
}

export default GeneralParameters
